import { useSearchParams } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import { PILLAR } from "contracts/enums/metrics";
import { contentFitnessDimensionForKey } from "contracts/report/content-fitness/lookups";
import { createMemo, Show } from "solid-js";
import { CONTENT_FITNESS_ORIGIN, SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { api } from "../../store/api";
import { singleParam } from "../../ui/searchParam";
import { createWebsiteStore } from "../website/store";
import { DimensionDetail } from "./ContentFitnessDimensionDetail";
import { UnknownDimension } from "./ScanContentFitnessParts";
import { ScanRouteSurface } from "./ScanRouteChrome";
import { ScanShell } from "./ScanShell";
import { signalsForPillar } from "./scanModel";

export function ScanContentFitnessDimension(props: {
  websiteId: string;
  dimensionKey: string;
}) {
  setScope(KEYBOARD_SCOPE.LIST);
  const [search] = useSearchParams();
  const context = createWebsiteStore(props.websiteId);
  const origin = createMemo(() =>
    singleParam(search.from) === "scorecard"
      ? CONTENT_FITNESS_ORIGIN.SCORECARD
      : CONTENT_FITNESS_ORIGIN.SUMMARY,
  );
  const data = () => context.data();
  const dimension = createMemo(() =>
    contentFitnessDimensionForKey(props.dimensionKey),
  );
  const signal = createMemo(() => {
    const value = data();
    return value
      ? (signalsForPillar(value, PILLAR.CONTENT_FITNESS).find(
          (item) => item.key === props.dimensionKey,
        ) ?? null)
      : null;
  });
  // One backend call returns every persisted assessment for this dimension in
  // the latest run (replaces the former per-page detail fan-out).
  const assessmentsQuery = useQuery(() => ({
    queryKey: ["content-fitness", props.websiteId, props.dimensionKey],
    queryFn: () =>
      api.contentFitnessDimension(props.websiteId, props.dimensionKey),
  }));

  return (
    <ScanShell
      context={context}
      active={
        origin() === CONTENT_FITNESS_ORIGIN.SCORECARD
          ? SCAN_SECTION_KEY.SIGNALS
          : SCAN_SECTION_KEY.SUMMARY
      }
    >
      <ScanRouteSurface class="signal-dimension-detail-route">
        <Show
          when={data()}
          keyed
          fallback={<div class="loading">Loading Content Fitness...</div>}
        >
          {(value) => (
            <Show
              when={dimension()}
              keyed
              fallback={
                <UnknownDimension
                  websiteId={props.websiteId}
                  dimensionKey={props.dimensionKey}
                  origin={origin()}
                />
              }
            >
              {(dimensionValue) => (
                <DimensionDetail
                  data={value}
                  dimension={dimensionValue}
                  signal={signal()}
                  assessments={assessmentsQuery.data?.assessments ?? []}
                  assessmentsLoading={assessmentsQuery.isPending}
                  websiteId={props.websiteId}
                  origin={origin()}
                />
              )}
            </Show>
          )}
        </Show>
      </ScanRouteSurface>
    </ScanShell>
  );
}
